// Single source of truth for shared factor types and pillar weight constants.
// Server (compute.ts), client rescorer (clientScore.ts), and UI (app/page.tsx)
// all import from here so a change to defaults can't drift across files.
//
// What this file owns:
//   - FactorConfig / QualityPillar / PillarMapping types
//   - DEFAULT_PILLAR_WEIGHTS — percentage form, used by UI and clientScore fallback
//   - DEFAULT_PILLAR_WEIGHTS_DECIMAL — decimal form, used inside scoreStocks()
//
// What this file does NOT own (intentional, today):
//   - PILLAR_FACTORS (server: peer-group split + per-factor weights) lives in compute.ts
//   - QUALITY_FACTORS (client: unified set, all weights = 1) lives in clientScore.ts
//   These have different *shapes* by design. Reconciling them is a model decision.

import type { StockFactors, PillarScores } from './types';

export type FactorConfig = { key: keyof StockFactors; invert: boolean; weight?: number };

// Quality pillars (excludes momentum, which is display-only and never weighted into qualityScore).
export type QualityPillar = Exclude<keyof PillarScores, 'momentum'>;

export type PillarMapping = {
  profitability: FactorConfig[];
  growth: FactorConfig[];
  safety: FactorConfig[];
  shareholderReturns: FactorConfig[];
  valuation: FactorConfig[];
  momentum: FactorConfig[];
};

// Pillar weights in percentage form. Used by:
//   - app/page.tsx (slider defaults, sent to ControlPanel)
//   - lib/clientScore.ts (rescoreStocks fallback when no slider weights passed)
// momentum is always 0: momentum is the Z-axis display pillar and intentionally
// has no influence on Quality (X-axis) selection.
export const DEFAULT_PILLAR_WEIGHTS: Record<keyof PillarScores, number> = {
  profitability: 45,
  growth: 20,
  safety: 15,
  shareholderReturns: 20,
  momentum: 0,
};

// Same defaults as decimals, used inside scoreStocks() in compute.ts where the
// internal blending math expects 0..1 weights, not percentages.
export const DEFAULT_PILLAR_WEIGHTS_DECIMAL: Record<QualityPillar, number> = {
  profitability: 0.45,
  growth: 0.20,
  safety: 0.15,
  shareholderReturns: 0.20,
};

// Iteration order for the four quality pillars (excludes momentum). Used by both
// server scoring and client rescorer so the iteration is consistent.
export const QUALITY_PILLAR_KEYS = ['profitability', 'growth', 'safety', 'shareholderReturns'] as const;
